import { type Nullable } from '@Types';
import { type BrandConfig, type Brand, type BrandOrInstitutionId } from 'AppBranding';
import { type ProgramPreferencesStateKey } from 'Careers';
import { type CompletionDocumentType } from 'Cohorts';
import { type AdditionalFinancingOption } from 'CohortSectionOffer';
import { type FormConfig } from 'FormConfigs';
import { type ProgramListItem } from 'Settings/angularModule/scripts/select_program/selectProgram.types';
import { type StudentSpotlightCarouselProps } from 'StudentSpotlight';

export enum ProgramField {
    ai_for_technical_leaders_cert = 'ai_for_technical_leaders_cert',
    ai_for_business_cert = 'ai_for_business_cert',
    bus_analytics_leaders_cert = 'bus_analytics_leaders_cert',
    career_network_only = 'career_network_only',
    cmo_cert = 'cmo_cert',
    cto_cert = 'cto_cert',
    data_science_foundations_cert = 'data_science_foundations_cert',
    executive_cert = 'executive_cert',
    // This is the legacy name for the program field and what is in the db for C-FIN-## programs
    fin_for_non_fin_managers_cert = 'fin_for_non_fin_managers_cert',
    founders_cert = 'founders_cert',
    jordanian_math = 'jordanian_math',
    learn_code_gpt_cert = 'learn_code_gpt_cert',
    master_of_business_administration = 'master_of_business_administration',
    master_of_science_in_business_analytics = 'master_of_science_in_business_analytics',
    master_of_science_in_software_engineering = 'master_of_science_in_software_engineering',
    prototype_gpt_cert = 'prototype_gpt_cert',
    the_business_certificate = 'the_business_certificate',
    tyb_ai_cert = 'tyb_ai_cert',
    tyb_blockchain_cert = 'tyb_blockchain_cert',
    valar_leadership_accelerator = 'valar_leadership_accelerator',
}

export enum ProgramType {
    mba = 'mba',
    mba_leadership = 'mba_leadership',
    emba = 'emba',
    emba_strategic_leadership = 'emba_strategic_leadership',
    msba = 'msba',
    msse = 'msse',
    msaie = 'msaie',
    vla = 'vla',
    vla_aspiring_managers = 'vla_aspiring_managers',
    career_network_only = 'career_network_only',
    the_business_certificate = 'the_business_certificate',
    jordanian_math = 'jordanian_math',
    demo = 'demo',
    external = 'external',
    tyb_ai_cert = 'tyb_ai_cert',
    tyb_blockchain_cert = 'tyb_blockchain_cert',
    learn_code_gpt_cert = 'learn_code_gpt_cert',
    // This is the legacy name for the program type and what is in the db for C-FIN-## programs
    fin_for_non_fin_managers_cert = 'fin_for_non_fin_managers_cert',
    bus_analytics_leaders_cert = 'bus_analytics_leaders_cert',
    data_science_foundations_cert = 'data_science_foundations_cert',
    prototype_gpt_cert = 'prototype_gpt_cert',
    cto_cert = 'cto_cert',
    founders_cert = 'founders_cert',
    executive_cert = 'executive_cert',
    cmo_cert = 'cmo_cert',
    ai_for_technical_leaders_cert = 'ai_for_technical_leaders_cert',
    ai_for_business_cert = 'ai_for_business_cert',
}

export enum ProgramFamily {
    master_of_business_administration = 'master_of_business_administration',
    master_of_science_in_business_analytics = 'master_of_science_in_business_analytics',
    master_of_science_in_software_engineering = 'master_of_science_in_software_engineering',
    valar_leadership_accelerator = 'valar_leadership_accelerator',
    career_network_only = 'career_network_only',
    the_business_certificate = 'the_business_certificate',
    jordanian_math = 'jordanian_math',
    exec_ed_certificate = 'exec_ed_certificate',
}

// Whenever supportsNetworkAccess is true, networkProgramTitle should be set
export type WithNetworkAccess = {
    supportsNetworkAccess: true;
    networkProgramTitle: string;
};
// Whenever supportsNetworkAccess is false, networkProgramTitle should be left null
export type WithoutNetworkAccess = {
    supportsNetworkAccess: false;
    networkProgramTitle: null;
};

export type ProgramTypeConfigProgramField = Nullable<ProgramField>;

// Some programs, such as our degree programs, require a user to manually apply to the program in order to receive a ProgramApplication.
// The primary reason this flag was added was to help delineate between programs that support the notion of having a ProgramApplication (`supportsProgramApplications`)
// as part of the admissions process, and programs that require a user to manually apply to the program in order to receive a ProgramApplication.
// IE: ExecEd programs have `supportsProgramApplications = true` and `supportsManuallyApplyingToProgram = false` because they can receive a ProgramApplication
// without manually applying via our application form.
// A program that requires manual application must define an applicationFormConfigs.
export type WithManualApplication = {
    supportsManuallyApplyingToProgram: true;
    applicationFormConfigs: FormConfig[];
};

export type WithoutManualApplication = {
    supportsManuallyApplyingToProgram: false;
    applicationFormConfigs: readonly [];
};

// Minimal cohort shape that the function-form ProgramTypeConfig fields below accept.
// Both the React POJO `Cohort` (`startDate: number`) and the Angular `CohortIguanaObject`
// (`startDate: Date`) satisfy this. Most function-form props ignore the cohort arg —
// only MSBA branches on `startDate` for the pre-MSBA78 carve-out, and the ExecEd cert
// builder reads `title` for accepted-screen / dashboard-box title transforms. Callers
// without cohort context (e.g. admin filters) pass `null`.
export type CohortContext = {
    programType: ProgramType;
    startDate: number | Date;
    title: string;
};

export type PostApplyInterviewModalLocaleKeys = {
    nextStepKey: string;
    selectivityMessageKey: string;
};

export type ProgramTypeConfig = {
    key: ProgramType;
    programField: ProgramTypeConfigProgramField;
    programFamily?: ProgramFamily;
    isMBA: boolean;
    isEMBA: boolean;
    isMSBA: boolean;
    isMSSE: boolean;
    isMSAIE: boolean;
    isExecEd: boolean;
    numMonths: number | null;
    isDegreeProgram: boolean;
    supportsCohortPreApproval: boolean;
    // The domain of the student email address assigned to students in this program (e.g.
    // `students.quantic.edu`). Only Quantic degree programs issue student email addresses; null otherwise.
    studentEmailDomain: Nullable<string>;
    // Whether the Settings > Account page shows the profile-photo upload section for this program.
    supportsAvatarUploadInAccountPage: boolean;
    fullTitle: (cohort: CohortContext | null) => string;
    institutionID: BrandOrInstitutionId;
    label: string;
    learnMoreUrlBrandConfigProperty?: keyof BrandConfig;
    marketingPageUrl?: string;
    openCoursesSidebarBoxContentKey?: string;
    applicationReceivedBoxPlaylistTitleKey?: string;
    applicationReceivedModalContinueCoursesKey?: string;
    programBoxNotAppliedMobileHtmlKey?: string;
    shortProgramTitle: string;
    supportsSchedule: boolean;
    // Schedule, schedule-status, and enrollment sidebar boxes shown on the student
    // dashboard. Two-month ExecEd certs (cycle 77+) hide all three.
    supportsScheduleSidebarBox: (cohort: CohortContext | null) => boolean;
    supportsScheduleStatusSidebarBox: (cohort: CohortContext | null) => boolean;
    supportsEnrollmentSidebar: (cohort: CohortContext | null) => boolean;
    supportsCourseSummary: boolean;
    branding: Brand;
    requiresTILADisclosure: boolean;
    supportsBotPage: boolean;
    completionDocumentType: CompletionDocumentType;
    requiresProctoring: boolean;
    showApplicationScholarshipEligibilityNotice: boolean;
    showFundingEmail: boolean;
    supportsBuyNowPayLater: boolean;
    supportsTranscriptDownload: boolean;
    supportsStudentDashboardConfig: boolean;
    supportsEnrollmentVerificationLetter: boolean;
    defaultAutoDeclineAdmissionOffersDaysOffset: number | null;
    programPreferenceStateKeys: ProgramPreferencesStateKey[];

    // We have several programs, such as VLA and MiyaMiya, that have a different admissions process than the rest of our programs.
    // The process for these programs involves granting a ProgramInclusion directly to the user without requiring pre-requisite ProgramApplication
    // and AdmissionOffer records. For these programs, and others like them, we set `supportsProgramApplications` to false. The value for this
    // property should match the corresponding `supports_program_applications` value in `program_type_config.rb`.
    supportsProgramApplications: boolean;

    // Users currently in a program of this type are allowed to initiate applications to other programs
    // (typically degree programs) via /settings/select-program. This is independent of whether THIS program
    // type itself accepts manual applications via the application form (see supportsManuallyApplyingToProgram
    // / WithManualApplication). For example, ExecEd programs do not accept manual applications (no
    // applicationFormConfigs) but their learners are allowed to apply to a degree program.
    usersCanInitiateProgramApplications: boolean;

    // NOTE: By default this is the same as shortProgramTitle, but some programs need "Certificate" or "Program"
    // added to the end when they are displayed alone (i.e. as a title/header, rather than in a sentence). In those cases,
    // we override standaloneShortProgramTitle.
    standaloneShortProgramTitle: string;
    transcriptProgramTitle?: (cohort: CohortContext | null) => string | null;
    // This information is only designed to display default payment values, i.e. "$14,250 for the 15-month program" in contexts where we do not
    // have an AdmissionOffer or TuitionContract, mainly inside the application forms.
    // DO NOT use this if you have an AdmissionOffer or TuitionContract to read payment information from.
    // When changing pricing, these values need to be baked and min-bumped before we take applications to Cohorts where pricing is changing.
    preOfferDefaultPaymentInformation?: {
        baseTuition: number;
        numMonthlyIntervals: number;
    };
    applicationProgramTypeHeader: string | null;
    welcomePackageConfiguration: WelcomePackageConfiguration | null;
    libraryDatabases: ('statista' | 'one_business')[];
    studentSpotlightCarouselProps: StudentSpotlightCarouselProps;
    selectProgramListItem: Nullable<ProgramListItem>;
    supportsAdditionalFinancingOptions: boolean;
    additionalFinancingOptions: AdditionalFinancingOption[];
    programBoxTitle?: (cohort: CohortContext | null) => string | null;
    programSwitcherMenuTitle: (cohort: CohortContext | null) => string;
    programAchievementGraphicProgramTitleLines: (cohort: CohortContext | null) => string[];
    acceptedScreenTitleHtml?: (cohort: CohortContext | null) => string | null;
    studentDashboardProgramBoxTitleHtml?: (cohort: CohortContext | null) => string | null;
    supportEmailDomain: string | null;
    supportEmailUsername: string | null;
    projectEmail?: string | null;
    enrollmentFaqLink: string | null;
    supportsWelcomeBox?: boolean;
    supportsGradebook: boolean;
    supportsDocumentUpload: boolean;
    // Whether students in this program may be asked to provide English language proficiency
    // documentation. Mirrors `requiresEnglishLanguageProficiency` on the Angular program types
    // (Cohorts/angularModule/scripts/schedulable.js).
    requiresEnglishLanguageProficiency: boolean;
    // Whether students in this program must provide a mailing address (for identity verification
    // and diploma delivery). Mirrors `requiresMailingAddress` in schedulable.js.
    requiresMailingAddress: boolean;
    supportsEditingCareerProfile: boolean;
    supportsResourcesTab: boolean;
    supportsAdmissionRounds: boolean;
    postApplyInterviewModalLocaleKeys: {
        instant: PostApplyInterviewModalLocaleKeys;
        optional: PostApplyInterviewModalLocaleKeys;
    };
} & (WithoutNetworkAccess | WithNetworkAccess) & // NOTE: if adding new discriminated unions here, you need to be sure to add them to ./buildConfig.ts because we're mapping over the ProgramTypeConfig type there!
    (WithoutManualApplication | WithManualApplication);

export type WelcomePackageStudent = {
    name: string;
    locationString: string;
    jobTitle: string;
    company: string;
    university: string;
    degree: string;
    avatarUrl: string;
};

export type WelcomePackageConfiguration = {
    badgeSrc: BrandConfig['programWelcomePackageBadge'];
    headlineKey: string;
    videoSrc: string | null;
    students: WelcomePackageStudent[];
};
